import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import "../css/booking.css";
import TherapistCard from "../components/TherapistCard";
import DateCard from "../components/DateCard";
import TimeSlotCard from "../components/TimeSlotCard";
import SessionDetail from "../components/SessionDetail/sessionDetail";
import AboutPhysica from "../components/About/aboutPhysica";
import BackHeader from "../components/Home/BackHeader";
import StepIndicator from "../components/Home/StepIndicator";
import Button from "../components/Button";
import useDraggableScroll from "../hooks";
import rightIcon from "../assets/images/right-arrow.png";
import instance from "../api/axios";
import mixpanel from "../mixpanel";
import {
  createHoldBooking,
  getAllTherapist,
  getAllTherapistInternalBooking,
  getTherapistInternalBooking,
} from "../api/route";
import { getHoldBookingDraft, buildHoldEndTime } from "../utils/holdBooking";

const normalizeSlot = (slot = {}) => {
  const time =
    slot.time ||
    slot.start_at ||
    slot.startAt ||
    slot.startTime ||
    slot.slot_time;
  const therapist_id =
    slot.therapist_id ??
    slot.therapistId ??
    slot.doctor_id ??
    slot.doctorId ??
    slot.dayschedule_id;

  return {
    ...slot,
    time,
    epochMs: time ? new Date(time).getTime() : null,
    therapist_id,
    blocked: slot.blocked ?? false,
  };
};

const normalizeSlotsByDate = (slotsByDate = {}) =>
  Object.fromEntries(
    Object.entries(slotsByDate).map(([date, slots]) => [
      date,
      Array.isArray(slots) ? slots.map((slot) => normalizeSlot(slot)) : [],
    ])
  );

const OpsHoldSlotPage = () => {
  const navigate = useNavigate();
  const { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove } =
    useDraggableScroll();
  const draft = getHoldBookingDraft();
  const today = useMemo(
    () =>
      new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })),
    []
  );

  const [showTherapistSection, setShowTherapistSection] = useState(true);
  const [loading, setLoading] = useState(false);
  const [creatingHold, setCreatingHold] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [therapists, setTherapists] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [selectedAllDoctorId, setSelectedAllDoctorId] = useState(null);
  const [selectedDoctorMeta, setSelectedDoctorMeta] = useState(null);
  const [allTimeSlotsByDate, setAllTimeSlotsByDate] = useState({});
  const [therapistSlotsByDate, setTherapistSlotsByDate] = useState({});
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(today.toISOString().split("T")[0]);
  const [allAvailable, setAllAvailable] = useState([]);
  const [morningSlots, setMorningSlots] = useState(false);
  const [noonSlots, setNoonSlots] = useState(false);
  const [eveningSlots, setEveningSlots] = useState(false);
  const [morningAllSlots, setMorningAllSlots] = useState(false);
  const [noonAllSlots, setNoonAllSlots] = useState(false);
  const [eveningAllSlots, setEveningAllSlots] = useState(false);

  const latestFetchKeyRef = useRef(0);
  const isFirstAllFetch = useRef(true);

  useEffect(() => {
    if (!draft) {
      navigate("/ops/hold-booking");
    }
  }, [draft, navigate]);

  const pinCode = draft?.pinCode;

  const getTherapistsByPincode = useCallback(async () => {
    if (!pinCode) return;
    setLoading(true);
    try {
      const response = await instance.get(`${getAllTherapist}?pinCode=${pinCode}`);
      setTherapists(response?.data?.therapists || []);
    } catch (error) {
      setErrorMessage("Unable to load therapists for this pincode.");
    } finally {
      setLoading(false);
    }
  }, [pinCode]);

  const getAllAvailability = useCallback(async () => {
    if (!pinCode) return;
    setLoading(true);
    try {
      const response = await instance.post(getAllTherapistInternalBooking, {
        pincode: pinCode,
      });
      const timeSlotsData = normalizeSlotsByDate(response?.data?.timeSlots || {});
      if (isFirstAllFetch.current) {
        const firstDate = Object.keys(timeSlotsData)[0];
        if (firstDate) setSelectedDate(firstDate);
        isFirstAllFetch.current = false;
      }

      const timing = Object.entries(timeSlotsData).map(([date, slots]) => ({
        date,
        slots: Array.isArray(slots) && slots.length > 0,
      }));

      setAllTimeSlotsByDate(timeSlotsData);
      setAllAvailable(timing);
    } catch (error) {
      setErrorMessage("Unable to load available slots right now.");
    } finally {
      setLoading(false);
    }
  }, [pinCode]);

  const getDoctorAvailability = useCallback(async () => {
    if (!selectedDoctorId || !pinCode) return;
    const fetchKey = ++latestFetchKeyRef.current;

    setLoading(true);
    try {
      const response = await instance.post(getTherapistInternalBooking, {
        doctor_id: selectedDoctorId,
        start_date: today.toISOString().split("T")[0],
        pincode: pinCode,
      });

      const slotsArray = response?.data?.data?.availability_by_date || [];
      const nextMap = {};
      slotsArray.forEach((day) => {
        nextMap[day.date] = (day.slots || []).map((slot) => ({
          time: slot.start_at,
          epochMs: slot.start_at ? new Date(slot.start_at).getTime() : null,
          available: true,
          blocked: false,
          therapist_id: selectedDoctorId,
        }));
      });

      if (fetchKey !== latestFetchKeyRef.current) return;
      setTherapistSlotsByDate((prev) => ({ ...prev, ...nextMap }));

      const timingUpdates = Object.keys(nextMap).map((date) => ({
        date,
        slots: (nextMap[date] || []).length > 0,
      }));
      setAllAvailable((prev) => {
        const copy = [...prev];
        timingUpdates.forEach((item) => {
          const index = copy.findIndex((value) => value.date === item.date);
          if (index > -1) copy[index] = item;
          else copy.push(item);
        });
        return copy;
      });
    } catch (error) {
      setErrorMessage("Unable to load therapist availability.");
    } finally {
      setLoading(false);
    }
  }, [pinCode, selectedDoctorId, today]);

  useEffect(() => {
    getTherapistsByPincode();
    getAllAvailability();
  }, [getAllAvailability, getTherapistsByPincode]);

  useEffect(() => {
    if (!selectedDoctorId) return;
    getDoctorAvailability();
  }, [selectedDoctorId, getDoctorAvailability]);

  useEffect(() => {
    const slots = selectedDoctorId
      ? therapistSlotsByDate[selectedDate] || []
      : allTimeSlotsByDate[selectedDate] || [];
    const hasInWindow = (start, end) =>
      slots.some((slot) => {
        const hour = new Date(slot.time).getHours();
        return hour >= start && hour < end;
      });

    setMorningSlots(hasInWindow(8, 12));
    setNoonSlots(hasInWindow(12, 16));
    setEveningSlots(hasInWindow(16, 21));
  }, [selectedDoctorId, selectedDate, allTimeSlotsByDate, therapistSlotsByDate]);

  useEffect(() => {
    const slots = allTimeSlotsByDate[selectedDate] || [];
    setMorningAllSlots(
      slots.some((slot) => {
        const hour = new Date(slot.time).getHours();
        return hour >= 8 && hour < 12;
      })
    );
    setNoonAllSlots(
      slots.some((slot) => {
        const hour = new Date(slot.time).getHours();
        return hour >= 12 && hour < 16;
      })
    );
    setEveningAllSlots(
      slots.some((slot) => {
        const hour = new Date(slot.time).getHours();
        return hour >= 16 && hour < 21;
      })
    );
  }, [allTimeSlotsByDate, selectedDate]);

  const handleTimeSlotSelection = useCallback((timeSlot) => {
    const slotId = String(timeSlot?.therapist_id ?? "");
    const resolvedDoctor = therapists.find((therapist) => {
      const ids = [
        therapist?.doctor_id,
        therapist?.homevisit_resource_id,
        therapist?.dayschedule_id,
      ]
        .filter(Boolean)
        .map(String);
      return ids.includes(slotId);
    });
    setSelectedTimeSlot(timeSlot);
    if (!selectedDoctorId) {
      setSelectedAllDoctorId(resolvedDoctor?.doctor_id || timeSlot.therapist_id);
      setSelectedDoctorMeta(resolvedDoctor || null);
    }

    mixpanel.track("Hold Booking Slot Selected", {
      time: timeSlot.time,
      therapist_id: timeSlot.therapist_id,
      blocked: !!timeSlot.blocked,
    });
  }, [selectedDoctorId, therapists]);

  const handleTherapistClick = (therapist) => {
    setSelectedTimeSlot(null);
    setTherapistSlotsByDate({});
    setSelectedDoctorId(therapist.doctor_id);
    setSelectedAllDoctorId(null);
    setSelectedDoctorMeta(therapist);
  };

  const renderAllTimeSlots = useMemo(
    () => (startTime, endTime) =>
      (allTimeSlotsByDate[selectedDate] || [])
        .filter((slot) => {
          const hour = new Date(slot.time).getHours();
          return hour >= startTime && hour < endTime;
        })
        .slice()
        .sort((a, b) => new Date(a.time) - new Date(b.time))
        .map((slot) => (
          <TimeSlotCard
            key={slot.time}
            time={slot.time}
            available={slot.available}
            blocked={slot.blocked}
            isSelected={selectedTimeSlot?.time === slot.time}
            onClick={() => handleTimeSlotSelection(slot)}
          />
        )),
    [allTimeSlotsByDate, selectedDate, selectedTimeSlot, handleTimeSlotSelection]
  );

  const renderDoctorTimeSlots = useMemo(
    () => (startTime, endTime) =>
      (therapistSlotsByDate[selectedDate] || [])
        .filter((slot) => {
          const hour = new Date(slot.time).getHours();
          return hour >= startTime && hour < endTime;
        })
        .slice()
        .sort((a, b) => new Date(a.time) - new Date(b.time))
        .map((slot) => (
          <TimeSlotCard
            key={slot.time}
            time={slot.time}
            available={slot.available}
            blocked={slot.blocked}
            isSelected={selectedTimeSlot?.time === slot.time}
            onClick={() => handleTimeSlotSelection(slot)}
          />
        )),
    [therapistSlotsByDate, selectedDate, selectedTimeSlot, handleTimeSlotSelection]
  );

  const formatDateToReadable = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();
    const getOrdinal = (value) => {
      const suffixes = ["th", "st", "nd", "rd"];
      const remainder = value % 100;
      return suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0];
    };
    return `${month} ${day}${getOrdinal(day)}, ${year}`;
  };

  const handleCreateHold = async () => {
    if (!selectedTimeSlot || !draft) return;

    const doctorIdToUse =
      selectedDoctorId || selectedAllDoctorId || selectedTimeSlot?.therapist_id;
    const resolvedDoctor =
      selectedDoctorMeta ||
      therapists.find((therapist) => String(therapist?.doctor_id) === String(doctorIdToUse));

    const therapistNameToUse =
      resolvedDoctor?.doctor_name || selectedDoctorMeta?.doctor_name || "Assigned Therapist";

    setCreatingHold(true);
    setErrorMessage("");
    try {
      const response = await instance.post(createHoldBooking, {
        patient_name: draft.name,
        patient_phone: draft.phone,
        pain_condition: draft.pain_condition,
        pain_score: draft.pain_score,
        pain_duration: draft.pain_duration,
        therapist_id: doctorIdToUse,
        therapist_name: therapistNameToUse,
        slot_date: selectedDate,
        start_time: selectedTimeSlot.time,
        end_time: buildHoldEndTime(selectedTimeSlot.time),
        amount: draft.amount,
      });

      mixpanel.track("Hold Booking Created", {
        hold_id: response?.data?.hold_id,
        therapist_id: doctorIdToUse,
        slot_time: selectedTimeSlot.time,
      });

      navigate(`/ops/hold-booking/summary/${response?.data?.hold_id}`, {
        state: {
          hold: response?.data?.hold,
          booking_link: response?.data?.booking_link,
          whatsapp_message: response?.data?.whatsapp_message,
        },
      });
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Unable to hold this slot right now."
      );
    } finally {
      setCreatingHold(false);
    }
  };

  const renderLoadingOverlay = () => {
    if (!creatingHold) return null;
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(255, 255, 255, 0.35)",
          zIndex: 1000,
          backdropFilter: "blur(2px)",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <style>
          {`
            .hold-loader {
              width: 50px;
              --b: 8px;
              aspect-ratio: 1;
              border-radius: 50%;
              padding: 1px;
              background: conic-gradient(#0000 10%, #FE8235) content-box;
              -webkit-mask:
                repeating-conic-gradient(#0000 0deg, #000 1deg 20deg, #0000 21deg 36deg),
                radial-gradient(farthest-side, #0000 calc(100% - var(--b) - 1px), #000 calc(100% - var(--b)));
              -webkit-mask-composite: destination-in;
              mask-composite: intersect;
              animation: holdSpin 1s infinite steps(10);
            }
            @keyframes holdSpin {
              to { transform: rotate(1turn); }
            }
          `}
        </style>
        <div className="hold-loader" />
        <div style={{ fontSize: "16px", fontWeight: 600, color: "#111827" }}>
          Holding the selected slot...
        </div>
      </div>
    );
  };

  if (!draft) return null;

  return (
    <>
      {renderLoadingOverlay()}
      <div className="BookingContainer">
        <div
          className="BookingWrapper"
          style={{
            backgroundImage: `url(${require("../assets/images/BackgroundBig.png")})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
        >
          <BackHeader onBack={() => navigate("/ops/hold-booking")} />
          <StepIndicator step={2} totalSteps={3} header="Select A Slot To Hold" />
          <AboutPhysica
            handleChooseTherapistClick={() => setShowTherapistSection((prev) => !prev)}
            setShowMore={() => {}}
            showMore={false}
            pinCode={pinCode}
          />
          {showTherapistSection && (
            <div className="ChooseTherapistContainer">
              <h1>Select A Therapist (Optional)</h1>
              <div className="ChooseTherapistWrapper">
                <div
                  className={
                    selectedAllDoctorId ||
                    (!selectedDoctorId && allTimeSlotsByDate[selectedDate]?.length > 0)
                      ? "AllTherapist selectedCard"
                      : "AllTherapist"
                  }
                  onClick={() => {
                    setSelectedTimeSlot(null);
                    setSelectedDoctorId(null);
                    setSelectedDoctorMeta(null);
                    getAllAvailability();
                  }}
                >
                  <p>ALL</p>
                  <p>Available</p>
                </div>
                <div
                  className="TherapistCards"
                  ref={ref}
                  onMouseDown={onMouseDown}
                  onMouseLeave={onMouseLeave}
                  onMouseUp={onMouseUp}
                  onMouseMove={onMouseMove}
                >
                  {therapists.map((item) => (
                    <TherapistCard
                      key={item.doctor_id}
                      setSelectedDoctorId={setSelectedDoctorId}
                      item={item}
                      onClick={() => handleTherapistClick(item)}
                      selectedDoctorId={selectedDoctorId}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          <SessionDetail />

          <div className="ChooseTherapistContainer">
            <h1>1. Select a Date</h1>
            <div className="TherapistCards DateCardWrapper2">
              {allAvailable.map((slot) => (
                <DateCard
                  selectedDate={selectedDate}
                  key={slot.date}
                  data={slot}
                  setselectedDate={setSelectedDate}
                  allTimeSlotsByDate={allTimeSlotsByDate}
                />
              ))}
            </div>
          </div>

          <div className="TimingHeading">
            <h1>2. Select Available Slot</h1>
            <h2 className="Datesunheading">
              Showing slots for:{" "}
              <span className="text-partner">{formatDateToReadable(selectedDate)}</span>
            </h2>

            {selectedDoctorId ? (
              <>
                {morningSlots && (
                  <div className="MorningDiv">
                    <div className="MorningDivHeading">
                      <p>Morning</p>
                      <p>8:00 AM - 12:00 PM</p>
                    </div>
                    <div className="TimeSlots">{renderDoctorTimeSlots(8, 12)}</div>
                  </div>
                )}
                {noonSlots && (
                  <div className="MorningDiv">
                    <div className="MorningDivHeading">
                      <p>Noon</p>
                      <p>12:00 PM - 4:00 PM</p>
                    </div>
                    <div className="TimeSlots">{renderDoctorTimeSlots(12, 16)}</div>
                  </div>
                )}
                {eveningSlots && (
                  <div className="MorningDiv">
                    <div className="MorningDivHeading">
                      <p>Evening</p>
                      <p>4:00 PM - 9:00 PM</p>
                    </div>
                    <div className="TimeSlots">{renderDoctorTimeSlots(16, 21)}</div>
                  </div>
                )}
              </>
            ) : (
              <>
                {morningAllSlots && (
                  <div className="MorningDiv">
                    <div className="MorningDivHeading">
                      <p>Morning</p>
                      <p>8:00 AM - 12:00 PM</p>
                    </div>
                    <div className="TimeSlots">{renderAllTimeSlots(8, 12)}</div>
                  </div>
                )}
                {noonAllSlots && (
                  <div className="MorningDiv">
                    <div className="MorningDivHeading">
                      <p>Noon</p>
                      <p>12:00 PM - 4:00 PM</p>
                    </div>
                    <div className="TimeSlots">{renderAllTimeSlots(12, 16)}</div>
                  </div>
                )}
                {eveningAllSlots && (
                  <div className="MorningDiv">
                    <div className="MorningDivHeading">
                      <p>Evening</p>
                      <p>4:00 PM - 9:00 PM</p>
                    </div>
                    <div className="TimeSlots">{renderAllTimeSlots(16, 21)}</div>
                  </div>
                )}
              </>
            )}
          </div>

          {errorMessage && (
            <div className="px-6 pb-2 text-center text-sm font-semibold text-red-600">
              {errorMessage}
            </div>
          )}

          <div className="flex justify-center mt-4">
            <Button
              btnName={
                <span className="flex items-center gap-2">
                  Hold This Slot
                  <img
                    src={rightIcon}
                    alt="arrow"
                    className="w-6 h-5 invert brightness-0"
                  />
                </span>
              }
              className={`offer-gradient text-center text-white font-semibold text-lg px-6 py-2 my-4 rounded-xl ${
                selectedTimeSlot ? "" : "opacity-50 cursor-not-allowed"
              }`}
              disabled={!selectedTimeSlot || loading || creatingHold}
              onClick={handleCreateHold}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default OpsHoldSlotPage;
